import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  MessageCircle, ShoppingCart, Star, ChevronLeft, Store, Package,
  CheckCircle, Phone, ArrowLeft, Loader2, CreditCard, Share2, Heart
} from "lucide-react";
import API, { formatError, getImageUrl } from "../api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { StarRating, formatPrice, useFavorites } from "../components/ProductCard";
import { formatWhatsApp } from "../utils/whatsapp";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [added, setAdded] = useState(false);

  // Mini-flow state
  const [orderStep, setOrderStep] = useState("idle"); // idle | phone_input | payment_info | whatsapp_ready
  const [clientPhone, setClientPhone] = useState("");
  const [clientName, setClientName] = useState("");
  const [currentOrder, setCurrentOrder] = useState(null);
  const [whatsappContact, setWhatsappContact] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState("");

  // Anonymous review state
  const [anonRating, setAnonRating] = useState(5);
  const [anonComment, setAnonComment] = useState("");
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Wishlist
  const { favorites, toggle: toggleFav } = useFavorites();
  const isFav = product ? favorites.includes(product._id) : false;
  const [sessionId] = useState(() => {
    let sid = sessionStorage.getItem("jkl_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("jkl_sid", sid);
    }
    return sid;
  });

  const { addToCart, cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      API.get(`/api/products/${id}`),
      API.get(`/api/reviews/product/${id}`),
    ]).then(([prod, rev]) => {
      setProduct(prod.data);
      setReviews(rev.data || []);
      // Track product view
      API.post("/api/events", {
        type: "product_view",
        product_id: id,
        vendor_id: prod.data?.vendor_id,
        session_id: sessionId,
      }).catch(() => {});
    }).catch(() => navigate("/catalogue")).finally(() => setLoading(false));
  }, [id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackEvent = async (type, extra = {}) => {
    try {
      await API.post("/api/events", {
        type,
        product_id: id,
        vendor_id: product?.vendor_id,
        session_id: sessionId,
        ...extra,
      });
    } catch (_) {}
  };

  const inCart = cart.some((i) => i.product_id === id);

  const handleAddToCart = () => {
    if (!product) return;
    addToCart({ ...product, vendor_shop_name: product.vendor?.shop_name });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
    trackEvent("add_to_cart", { product_name: product.name, price: product.price });
  };

  const handleInitiateOrder = async () => {
    if (!clientPhone.trim()) return;
    setOrderLoading(true);
    setOrderError("");
    try {
      const { data } = await API.post("/api/orders/anonymous-contact", {
        vendor_id: product.vendor_id,
        product_id: id,
        product_name: product.name,
        product_price: product.price,
        product_image: product.images?.[0] || null,
        client_phone: clientPhone.trim(),
        client_name: clientName.trim() || undefined,
        session_id: sessionId,
      });
      setCurrentOrder(data);
      setOrderStep("payment_info");
      trackEvent("order_initiated", { order_id: data._id, client_phone: clientPhone.trim() });
    } catch (e) {
      setOrderError(formatError(e));
    } finally {
      setOrderLoading(false);
    }
  };

  const handleDeclarePayment = async () => {
    if (!currentOrder?._id) return;
    setOrderLoading(true);
    setOrderError("");
    try {
      const { data } = await API.put(`/api/orders/${currentOrder._id}/declare-payment`);
      setWhatsappContact(data.vendor_contact);
      setOrderStep("whatsapp_ready");
      trackEvent("payment_declared", { order_id: currentOrder._id });
    } catch (e) {
      setOrderError(formatError(e));
    } finally {
      setOrderLoading(false);
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/auth"); return; }
    setReviewLoading(true);
    setReviewError("");
    try {
      const { data } = await API.post("/api/reviews", { product_id: id, rating: reviewRating, comment: reviewText });
      setReviews((prev) => [data, ...prev]);
      setReviewText("");
      setReviewRating(5);
      const prod = await API.get(`/api/products/${id}`);
      setProduct(prod.data);
    } catch (e) {
      setReviewError(formatError(e));
    } finally {
      setReviewLoading(false);
    }
  };

  const handleAnonReview = async (e) => {
    e.preventDefault();
    if (!currentOrder?._id || reviewSubmitted) return;
    setReviewSubmitting(true);
    try {
      await API.post("/api/reviews/anonymous", {
        order_id: currentOrder._id,
        product_id: id,
        vendor_id: product.vendor_id,
        rating: anonRating,
        comment: anonComment.trim() || undefined
      });
      setReviewSubmitted(true);
      const prod = await API.get(`/api/products/${id}`);
      setProduct(prod.data);
      const revs = await API.get(`/api/reviews/product/${id}`);
      setReviews(revs.data || []);
    } catch {
      // Silently ignore (already reviewed or order not eligible)
      setReviewSubmitted(true);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    const text = `Découvrez "${product.name}" à ${formatPrice(product.price)} sur Jokkoleen !\n${url}`;
    if (navigator.share) {
      navigator.share({ title: product.name, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#FF9900] border-t-transparent" />
    </div>
  );

  if (!product) return null;

  const images = product.images?.length ? product.images : [];
  const vendor = product.vendor;

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-5xl mx-auto px-4 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-xs text-gray-500 mb-4">
          <Link to="/" className="hover:text-[#FF9900]">Accueil</Link>
          <span>/</span>
          <Link to="/catalogue" className="hover:text-[#FF9900]">Catalogue</Link>
          <span>/</span>
          <span className="text-gray-700 truncate max-w-[200px]">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Images */}
          <div>
            <div className="aspect-square rounded-md overflow-hidden bg-white border border-gray-200">
              {images.length > 0 ? (
                <img
                  src={getImageUrl(images[imgIdx])}
                  alt={product.name}
                  className="w-full h-full object-contain p-2"
                  onError={(e) => { e.target.src = "https://via.placeholder.com/400?text=Image"; }}
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <Package size={80} />
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`w-14 h-14 rounded border-2 overflow-hidden shrink-0 transition-all ${i === imgIdx ? "border-[#FF9900]" : "border-gray-200"}`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://via.placeholder.com/60"; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <h1 className="text-2xl font-extrabold text-[#232F3E] leading-tight flex-1" data-testid="product-title">{product.name}</h1>
              <div className="flex items-center gap-1.5 shrink-0 mt-1">
                <button
                  onClick={() => toggleFav(product._id)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all ${isFav ? "bg-red-50 border-red-200" : "bg-white border-gray-200 hover:border-red-200"}`}
                  data-testid="product-favorite-btn"
                  aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
                >
                  <Heart size={16} className={isFav ? "text-red-500 fill-red-500" : "text-gray-400"} />
                </button>
                <button
                  onClick={handleShare}
                  className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-[#25D366] hover:text-[#25D366] transition-all"
                  data-testid="product-share-btn"
                  aria-label="Partager"
                >
                  <Share2 size={15} className="text-gray-500" />
                </button>
              </div>
            </div>

            {product.total_reviews > 0 && (
              <div className="flex items-center gap-2">
                <StarRating rating={product.rating} size={14} />
                <span className="text-sm text-gray-500">{product.rating}/5 ({product.total_reviews} avis)</span>
              </div>
            )}

            <p className="text-3xl font-black text-[#232F3E]" data-testid="product-price-detail">{formatPrice(product.price)}</p>

            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded font-medium ${product.stock > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {product.stock > 0 ? `En stock (${product.stock})` : "Épuisé"}
              </span>
            </div>

            {/* CTA — Mini-flow WhatsApp-first */}
            <div className="space-y-2 pt-1">
              {product.stock === 0 ? (
                <button
                  disabled
                  className="w-full bg-gray-100 text-gray-400 font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 cursor-not-allowed"
                  data-testid="product-contact-btn"
                >
                  <Package size={16} /> Épuisé
                </button>
              ) : orderStep === "idle" ? (
                <button
                  onClick={() => { trackEvent("click_order"); setOrderStep("phone_input"); setOrderError(""); }}
                  className="w-full bg-[#25D366] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 hover:bg-[#20BD5A] transition-colors"
                  data-testid="product-contact-btn"
                >
                  <MessageCircle size={16} /> Commander sur WhatsApp
                </button>
              ) : orderStep === "phone_input" ? (
                <div className="bg-white border border-green-200 rounded-xl p-4 space-y-3" data-testid="phone-input-step">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setOrderStep("idle"); setOrderError(""); }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="back-btn"
                    >
                      <ArrowLeft size={15} />
                    </button>
                    <p className="text-sm font-bold text-[#232F3E]">Vos coordonnées</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Numéro de téléphone *</label>
                    <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:border-[#25D366] transition-colors">
                      <Phone size={14} className="text-gray-400 shrink-0" />
                      <input
                        type="tel"
                        placeholder="ex: 77 123 45 67"
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleInitiateOrder()}
                        className="flex-1 text-sm outline-none bg-transparent"
                        data-testid="client-phone-input"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      Prénom <span className="text-gray-400 font-normal">(optionnel)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="ex: Aminata"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleInitiateOrder()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#25D366] transition-colors"
                      data-testid="client-name-input"
                    />
                  </div>
                  {orderError && <p className="text-red-500 text-xs" data-testid="order-error">{orderError}</p>}
                  <button
                    onClick={handleInitiateOrder}
                    disabled={!clientPhone.trim() || orderLoading}
                    className="w-full bg-[#25D366] text-white font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#20BD5A] transition-colors"
                    data-testid="initiate-order-btn"
                  >
                    {orderLoading ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                    {orderLoading ? "Traitement..." : "Continuer"}
                  </button>
                </div>
              ) : orderStep === "payment_info" ? (
                <div className="bg-white border border-blue-200 rounded-xl p-4 space-y-3" data-testid="payment-info-step">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <CreditCard size={13} className="text-blue-600" />
                    </div>
                    <p className="text-sm font-bold text-[#232F3E]">Informations de paiement</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Envoyez <span className="font-bold text-[#232F3E]">{formatPrice(product.price)}</span> au vendeur via :
                  </p>

                  {currentOrder?.vendor_payment_info?.phone_wave && (
                    <div className="flex items-center gap-3 bg-[#E8F7FF] rounded-lg px-3 py-2.5" data-testid="wave-payment-info">
                      <div className="w-7 h-7 rounded-full bg-[#1CBBEE] flex items-center justify-center shrink-0">
                        <span className="text-white text-[10px] font-black">W</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-500 font-medium">Wave</p>
                        <p className="text-sm font-black text-[#232F3E]">{currentOrder.vendor_payment_info.phone_wave}</p>
                      </div>
                      <button
                        onClick={() => navigator.clipboard?.writeText(currentOrder.vendor_payment_info.phone_wave)}
                        className="text-xs text-[#1CBBEE] font-semibold shrink-0"
                        data-testid="copy-wave-btn"
                      >
                        Copier
                      </button>
                    </div>
                  )}

                  {currentOrder?.vendor_payment_info?.phone_orange && (
                    <div className="flex items-center gap-3 bg-[#FFF3E0] rounded-lg px-3 py-2.5" data-testid="orange-payment-info">
                      <div className="w-7 h-7 rounded-full bg-[#FF6600] flex items-center justify-center shrink-0">
                        <span className="text-white text-[10px] font-black">O</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-500 font-medium">Orange Money</p>
                        <p className="text-sm font-black text-[#232F3E]">{currentOrder.vendor_payment_info.phone_orange}</p>
                      </div>
                      <button
                        onClick={() => navigator.clipboard?.writeText(currentOrder.vendor_payment_info.phone_orange)}
                        className="text-xs text-[#FF6600] font-semibold shrink-0"
                        data-testid="copy-orange-btn"
                      >
                        Copier
                      </button>
                    </div>
                  )}

                  {!currentOrder?.vendor_payment_info?.phone_wave && !currentOrder?.vendor_payment_info?.phone_orange && (
                    <p className="text-xs text-gray-400 text-center py-1">
                      Le vendeur vous communiquera ses coordonnées de paiement sur WhatsApp.
                    </p>
                  )}

                  {orderError && <p className="text-red-500 text-xs" data-testid="order-error">{orderError}</p>}
                  <button
                    onClick={handleDeclarePayment}
                    disabled={orderLoading}
                    className="w-full bg-[#232F3E] text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#37475A] transition-colors"
                    data-testid="declare-payment-btn"
                  >
                    {orderLoading ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                    {orderLoading ? "Vérification..." : "J'ai effectué le paiement"}
                  </button>
                </div>
              ) : orderStep === "whatsapp_ready" ? (
                <div className="space-y-3" data-testid="whatsapp-ready-step">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                        <CheckCircle size={13} className="text-green-600" />
                      </div>
                      <p className="text-sm font-bold text-green-700">Paiement déclaré !</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      Contactez le vendeur sur WhatsApp pour confirmer votre commande :
                    </p>
                    {whatsappContact?.whatsapp_number ? (
                      <a
                        href={`https://wa.me/${formatWhatsApp(whatsappContact.whatsapp_number)}?text=${encodeURIComponent(`Bonjour, j'ai effectué mon paiement de ${formatPrice(product.price)} pour "${product.name}" sur Jokkoleen. Réf: #${currentOrder?._id?.slice(-6).toUpperCase()}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent("whatsapp_opened", { order_id: currentOrder?._id })}
                        className="w-full bg-[#25D366] text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-[#20BD5A] transition-colors"
                        data-testid="whatsapp-vendor-btn"
                      >
                        <MessageCircle size={15} /> Contacter le vendeur sur WhatsApp
                      </a>
                    ) : (
                      <p className="text-xs text-gray-500 text-center py-1">
                        Le vendeur va vous contacter au <span className="font-bold">{clientPhone}</span>.
                      </p>
                    )}
                  </div>
                  {/* Anonymous review widget */}
                  {!reviewSubmitted ? (
                    <form onSubmit={handleAnonReview} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="anon-review-form">
                      <p className="text-sm font-semibold text-[#232F3E]">Comment s'est passée votre commande ?</p>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map((s) => (
                          <button key={s} type="button" onClick={() => setAnonRating(s)} className="transition-transform hover:scale-110" data-testid={`anon-star-${s}`}>
                            <Star size={26} className={s <= anonRating ? "text-[#FF9900] fill-[#FF9900]" : "text-gray-300"} />
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={anonComment}
                        onChange={(e) => setAnonComment(e.target.value)}
                        placeholder="Commentaire optionnel..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-[#FF9900] outline-none resize-none"
                        data-testid="anon-review-comment"
                      />
                      <button type="submit" disabled={reviewSubmitting} className="w-full bg-[#FF9900] text-white font-bold py-2.5 rounded-lg text-sm hover:bg-[#E68A00] disabled:opacity-60 transition-colors" data-testid="submit-anon-review-btn">
                        {reviewSubmitting ? "Envoi..." : "Publier mon avis"}
                      </button>
                    </form>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center" data-testid="review-submitted-msg">
                      <CheckCircle size={16} className="text-green-500 mx-auto mb-1" />
                      <p className="text-sm font-semibold text-green-700">Merci pour votre avis !</p>
                    </div>
                  )}
                </div>
              ) : null}

              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className={`w-full font-medium py-2.5 rounded-xl border transition-colors text-sm flex items-center justify-center gap-2 ${
                  product.stock === 0 ? "bg-gray-50 text-gray-300 cursor-not-allowed border-gray-100" :
                  added ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700"
                }`}
                data-testid="product-add-to-cart"
              >
                {added ? <><CheckCircle size={14} /> Ajouté au panier</> : <><ShoppingCart size={14} /> Ajouter au panier</>}
              </button>
            </div>

            {/* Vendor card */}
            {vendor && (
              <Link to={`/boutique/${product.vendor_id}`} className="flex items-center gap-3 bg-white border border-gray-200 rounded-md p-3 hover:border-[#FF9900] transition-colors" data-testid="vendor-shop-link">
                <div className="w-10 h-10 rounded-full bg-[#FF9900] flex items-center justify-center shrink-0">
                  {vendor.avatar ? (
                    <img src={getImageUrl(vendor.avatar)} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <Store size={18} className="text-[#111827]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#232F3E] truncate">{vendor.shop_name}</p>
                  {vendor.total_reviews > 0 && (
                    <div className="flex items-center gap-1">
                      <StarRating rating={vendor.rating} size={10} />
                      <span className="text-xs text-gray-500">({vendor.total_reviews})</span>
                    </div>
                  )}
                </div>
                <ChevronLeft size={14} className="text-gray-400 rotate-180" />
              </Link>
            )}
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="mt-6 bg-white border border-gray-200 rounded-md p-4">
            <h2 className="font-extrabold text-[#232F3E] text-lg mb-2">Description</h2>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{product.description}</p>
          </div>
        )}

        {/* Reviews */}
        <div className="mt-6 bg-white border border-gray-200 rounded-md p-4">
          <h2 className="font-extrabold text-[#232F3E] text-lg mb-3">
            Avis clients ({reviews.length})
          </h2>

          {user?.role === "customer" && (
            <form onSubmit={handleReview} className="mb-4 pb-4 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-2">Laisser un avis</p>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setReviewRating(s)}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${s}`}
                  >
                    <Star
                      size={24}
                      className={s <= reviewRating ? "text-[#FF9900] fill-[#FF9900]" : "text-gray-300"}
                    />
                  </button>
                ))}
              </div>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Votre commentaire (optionnel)..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none resize-none"
                data-testid="review-comment-input"
              />
              {reviewError && <p className="text-red-500 text-xs mt-1">{reviewError}</p>}
              <button
                type="submit"
                disabled={reviewLoading}
                className="mt-2 bg-[#FF9900] text-[#111827] font-bold px-4 py-2 rounded-md text-sm hover:bg-[#E68A00] disabled:opacity-60"
                data-testid="submit-review-btn"
              >
                {reviewLoading ? "Envoi..." : "Publier mon avis"}
              </button>
            </form>
          )}

          {reviews.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">Aucun avis pour l'instant. Soyez le premier !</p>
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r._id} className="border-b border-gray-100 pb-3 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm text-[#232F3E]">{r.customer_name}</span>
                    <StarRating rating={r.rating} size={11} />
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(r.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-gray-600 leading-relaxed">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
